import { useMemo } from "react";
import {
  type Control,
  type FieldValues,
  type Path,
  useController,
} from "react-hook-form";
import type { TypedWrappedFieldProps } from "redux-form";

import type { EnhanceElementProps } from "@braintrust/braintrust-ui-components";
import {
  Box,
  Button,
  Stack,
  Typography,
} from "@braintrust/braintrust-ui-components";
import { MenuArrowDownIcon } from "@braintrust/braintrust-ui-components/Icons";
import { getPaymentTypeOrder } from "@js/apps/jobs/apps/create-job/components/rate-section-module/payment-type-field/helpers";
import { violetMenuItemSx } from "@js/components/violet-menu-item";
import { DollarNumberField, MenuSelectField } from "@js/forms/fields";
import { RhfDollarNumberField } from "@js/rhf/fields/dollar-number";
import { RhfMenuSelect } from "@js/rhf/fields/menu-select";
import { enumToOptions } from "@js/utils";

import { JOB_PAYMENT_TYPE_LABELS } from "../../constants";
import type { PaymentTypeObject } from "../../types";

type AmountAndPaymentTypeFieldProps = {
  payment_amount: TypedWrappedFieldProps<string>;
  payment_type: TypedWrappedFieldProps<EnumType<typeof ENUMS.JobPaymentType>>;
};

const PLACEHOLDERS: PaymentTypeObject = {
  [ENUMS.JobPaymentType.HOURLY]: "100",
  [ENUMS.JobPaymentType.MONTHLY]: "10,000",
  [ENUMS.JobPaymentType.ANNUAL]: "100,000",
  [ENUMS.JobPaymentType.FIXED_PRICE]: "10,000",
  [ENUMS.JobPaymentType.FIXED_PROJECT]: "10,000",
  [ENUMS.JobPaymentType.PER_TASK]: "100",
} as const;

type AnchorProps = {
  anchorProps: EnhanceElementProps;
  isOpen: boolean;
};

const renderAnchor = (
  { anchorProps, isOpen }: AnchorProps,
  paymentType: EnumType<typeof ENUMS.JobPaymentType>,
) => {
  const {
    "aria-controls": ariaControls,
    "aria-haspopup": ariaHasPopup,
    ...props
  } = anchorProps;

  return (
    <Button
      aria-controls={ariaControls}
      aria-haspopup={ariaHasPopup}
      style={{ padding: "6px 8px" }}
      shape="squared"
      variant="transparent-with-text-hover"
      {...props}
    >
      {JOB_PAYMENT_TYPE_LABELS[paymentType]}
      <Box sx={{ ml: 1, pt: 0.5 }}>
        <MenuArrowDownIcon
          sx={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}
        />
      </Box>
    </Button>
  );
};

export const AmountAndPaymentTypeField = ({
  payment_amount,
  payment_type,
}: AmountAndPaymentTypeFieldProps) => {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box sx={{ width: 138 }}>
        <DollarNumberField
          id="pay-rate-id"
          size="large"
          placeholder={PLACEHOLDERS[payment_type.input.value]}
          hideError
          {...payment_amount}
          slotProps={{
            input: {
              style: { boxShadow: `var(--elevation-4)`, borderRadius: "9px" },
            },
          }}
        />
      </Box>
      <MenuSelectField
        {...payment_type}
        menuItemSx={violetMenuItemSx}
        disableMenuItemRipple
        type="radio"
        anchor={(props: AnchorProps) =>
          renderAnchor(props, payment_type.input.value)
        }
        options={enumToOptions(JOB_PAYMENT_TYPE_LABELS)}
        sx={{ li: { padding: "10px 12px !important" } }}
      />
    </Box>
  );
};

export type RhfAmountAndPaymentTypeBase = FieldValues & {
  payment_amount: string | undefined;
  payment_type: EnumType<typeof ENUMS.JobPaymentType>;
};

type RhfAmountAndPaymentTypeFieldProps<T extends FieldValues> = {
  radius?: string;
  control: Control<T>;
  jobType: EnumType<typeof ENUMS.JobType> | undefined;
};

export const RhfAmountAndPaymentType = <T extends FieldValues>({
  radius,
  control,
  jobType,
}: RhfAmountAndPaymentTypeFieldProps<T>) => {
  const { field: paymentTypeField } = useController({
    control,
    name: "payment_type" as Path<T>,
  });
  const { fieldState: paymentAmountState } = useController({
    control,
    name: "payment_amount" as Path<T>,
  });
  const paymentType = paymentTypeField.value as EnumType<
    typeof ENUMS.JobPaymentType
  >;
  const options = useMemo(() => {
    const isFreelanceJob = jobType === ENUMS.JobType.FREELANCE;

    const order = getPaymentTypeOrder(jobType);

    return order
      .filter((option) => {
        if (option === ENUMS.JobPaymentType.FIXED_PROJECT && !isFreelanceJob) {
          return false;
        }
        if (option === ENUMS.JobPaymentType.PER_TASK && !isFreelanceJob) {
          return false;
        }

        return true;
      })
      .map((value) => {
        return {
          value,
          label: JOB_PAYMENT_TYPE_LABELS[value],
        };
      });
  }, [jobType]);
  const errorMessage = paymentAmountState?.error?.message;
  const error = paymentAmountState.invalid ? errorMessage : undefined;
  return (
    <Stack sx={{ gap: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{ width: 138, borderRadius: "9px" }}>
          <RhfDollarNumberField
            control={control}
            hideError
            id="pay-rate-id"
            size="large"
            radius={radius}
            placeholder={PLACEHOLDERS[paymentType]}
            name={"payment_amount" as Path<T>}
            slotProps={{
              input: {
                style: { boxShadow: `var(--elevation-4)`, borderRadius: "9px" },
              },
            }}
          />
        </Box>
        <RhfMenuSelect
          control={control}
          id="payment-type-id"
          name={"payment_type" as Path<T>}
          menuItemSx={violetMenuItemSx}
          disableMenuItemRipple
          type="radio"
          anchor={(props: AnchorProps) => renderAnchor(props, paymentType)}
          options={options}
          sx={{ li: { padding: "10px 12px !important" } }}
        />
      </Box>

      {!!error && (
        <Typography component="p" error sx={{ fontSize: "13px !important" }}>
          {error}
        </Typography>
      )}
    </Stack>
  );
};
